import React from 'react';
import { DollarSign, FileText, ParkingCircle, TicketPercent, PiggyBank, Wrench } from 'lucide-react';

/**
 * Компонент дополнительных расходов для калькулятора расхода топлива
 */
const AdditionalCostsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  if (!formData.includeAdditionalCosts) {
    return (
      <div className="mb-6">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeAdditionalCosts"
            checked={formData.includeAdditionalCosts}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Учитывать дополнительные расходы
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="includeAdditionalCosts"
            checked={formData.includeAdditionalCosts}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white flex items-center">
            <DollarSign className="w-5 h-5 mr-1 text-purple-500" />
            Дополнительные расходы
          </span>
        </label>
      </div>

      <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
        <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
          Укажите сумму дополнительных расходов для поездки (в рублях):
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Техническое обслуживание */}
          <div>
            <label htmlFor="additionalCosts.maintenance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Wrench className="w-4 h-4 mr-2 text-purple-500" />
              Техническое обслуживание
            </label>
            <input
              type="text"
              id="additionalCosts.maintenance"
              name="additionalCosts.maintenance"
              value={formData.additionalCosts.maintenance}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['additionalCosts.maintenance'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['additionalCosts.maintenance'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['additionalCosts.maintenance']}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Амортизация, ТО, замена масла и расходников
            </p>
          </div>

          {/* Страхование */}
          <div>
            <label htmlFor="additionalCosts.insurance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-purple-500" />
              Страхование
            </label>
            <input
              type="text"
              id="additionalCosts.insurance"
              name="additionalCosts.insurance"
              value={formData.additionalCosts.insurance}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['additionalCosts.insurance'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['additionalCosts.insurance'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['additionalCosts.insurance']}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              ОСАГО, КАСКО в пересчете на поездку
            </p>
          </div>

          {/* Налоги */}
          <div>
            <label htmlFor="additionalCosts.tax" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <PiggyBank className="w-4 h-4 mr-2 text-purple-500" />
              Налоги
            </label>
            <input
              type="text"
              id="additionalCosts.tax"
              name="additionalCosts.tax"
              value={formData.additionalCosts.tax}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['additionalCosts.tax'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['additionalCosts.tax'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['additionalCosts.tax']}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Транспортный налог в пересчете на поездку
            </p>
          </div>

          {/* Парковка */}
          <div>
            <label htmlFor="additionalCosts.parking" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <ParkingCircle className="w-4 h-4 mr-2 text-purple-500" />
              Парковка
            </label>
            <input
              type="text"
              id="additionalCosts.parking"
              name="additionalCosts.parking"
              value={formData.additionalCosts.parking}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['additionalCosts.parking'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['additionalCosts.parking'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['additionalCosts.parking']}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Стоимость парковки в городе
            </p>
          </div>

          {/* Платные дороги */}
          <div>
            <label htmlFor="additionalCosts.toll" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <TicketPercent className="w-4 h-4 mr-2 text-purple-500" />
              Платные дороги
            </label>
            <input
              type="text"
              id="additionalCosts.toll"
              name="additionalCosts.toll"
              value={formData.additionalCosts.toll}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['additionalCosts.toll'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['additionalCosts.toll'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['additionalCosts.toll']}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Платные участки трасс, мосты и тоннели
            </p>
          </div>

          {/* Прочие расходы */}
          <div>
            <label htmlFor="additionalCosts.other" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Прочие расходы
            </label>
            <input
              type="text"
              id="additionalCosts.other"
              name="additionalCosts.other"
              value={formData.additionalCosts.other}
              onChange={handleInputChange}
              className={`form-input w-full ${errors['additionalCosts.other'] ? 'input-error' : ''}`}
              placeholder="0"
            />
            {errors['additionalCosts.other'] && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors['additionalCosts.other']}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Мойка, шиномонтаж и прочие расходы
            </p>
          </div>
        </div>
        
        <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-700">
          <div className="flex items-start">
            <DollarSign className="w-5 h-5 text-purple-500 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                Полная стоимость владения автомобилем
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Для точной оценки стоимости поездки рекомендуется учитывать не только затраты на топливо, 
                но и дополнительные расходы. В России в среднем они составляют около 3-5 ₽ на километр пробега 
                для бюджетных автомобилей и 7-12 ₽ для автомобилей среднего и премиального классов.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalCostsSection;